/**
 * Challenge-type-first grouping for the Accounts page.
 *
 * The operator runs batches across firms with different challenge
 * structures (instant funding vs 1-step vs 2-step vs 3-step) and wants
 * each class surfaced separately so phase-parallel accounts end up
 * next to each other instead of interleaved with unrelated firms. The
 * functions here are pure — no React, no API. They take the mapped
 * `Account` view-model (from `Accounts.tsx`) and return the shape the
 * page renders.
 */

import type { AccountRow } from "./api/accounts";

export type ChallengeCategory =
  | "futures"
  | "instant"
  | "1-step"
  | "2-step"
  | "3-step"
  | "other";

export const CATEGORY_ORDER: ChallengeCategory[] = [
  "futures",
  "instant",
  "1-step",
  "2-step",
  "3-step",
  "other",
];

export const CATEGORY_LABEL: Record<ChallengeCategory, string> = {
  futures: "Futures Evaluations",
  instant: "Instant Funding",
  "1-step": "1 Step",
  "2-step": "2 Steps",
  "3-step": "3 Steps",
  other: "Other",
};

export interface PhaseDef {
  /** The `phase` column value we match against (step1 / step2 / step3 / funded). */
  key: string;
  /** Human label for the subgroup header. */
  label: string;
}

/**
 * Per-category phase layout. Instant Funding skips the evaluation
 * phases; the step counts grow with the challenge. The empty phase
 * columns still render so the eye reads the same structure across
 * firms of the same type.
 */
export const PHASES_BY_CATEGORY: Record<ChallengeCategory, PhaseDef[]> = {
  futures: [
    { key: "step1", label: "Evaluation" },
    { key: "funded", label: "Funded" },
  ],
  instant: [{ key: "funded", label: "Funded" }],
  "1-step": [
    { key: "step1", label: "Evaluation" },
    { key: "funded", label: "Funded" },
  ],
  "2-step": [
    { key: "step1", label: "Step 1" },
    { key: "step2", label: "Step 2" },
    { key: "funded", label: "Funded" },
  ],
  "3-step": [
    { key: "step1", label: "Step 1" },
    { key: "step2", label: "Step 2" },
    { key: "step3", label: "Step 3" },
    { key: "funded", label: "Funded" },
  ],
  other: [{ key: "__other__", label: "Accounts" }],
};

/**
 * Decide which top-level category a row belongs to. `challenge_type`
 * is the authoritative signal (operator-confirmed in the rules form);
 * `steps` is a reliable fallback when the row was added before the
 * rules step was completed. Everything else falls into "other".
 */
export function categorizeAccount(
  r: Pick<AccountRow, "challenge_type" | "steps" | "platform_type">,
): ChallengeCategory {
  if (r.platform_type === "tradovate" || r.platform_type === "ninjatrader" || r.platform_type === "blackarrow") {
    return "futures";
  }
  if (r.challenge_type === "instant") return "instant";
  if (r.challenge_type === "1-step" || r.steps === 1) return "1-step";
  if (r.challenge_type === "2-step" || r.steps === 2) return "2-step";
  if (r.challenge_type === "3-step" || r.steps === 3) return "3-step";
  return "other";
}

/**
 * The view-model the section component consumes. Keep it loose —
 * callers downcast to their own richer Account type on render since
 * the grouping logic only needs category + phase + outcome keys to
 * slot rows into buckets.
 */
export interface GroupableAccount {
  phase: string;
  challengeType: string | null;
  steps: number | null;
  platformType: string | null;
  outcome: "passed" | "breached" | null;
}

export interface AccountGroup<T extends GroupableAccount> {
  category: ChallengeCategory;
  label: string;
  phases: PhaseDef[];
  /** Map keyed by `phase.key` → accounts currently in that phase.
   *  Always includes every phase the category defines, even when
   *  empty, so the UI can render a placeholder cell. */
  active: Record<string, T[]>;
  /** Terminal accounts (outcome set) grouped under an archive
   *  regardless of their residual phase. */
  archived: T[];
  totals: { active: number; archived: number };
}

/**
 * Bucket accounts into challenge-type groups. Hides any category that
 * has zero active AND zero archived accounts — no empty 3-step section
 * takes up space until the operator actually has a 3-step row.
 *
 * Archive = anything with `outcome != null`. The outcome is terminal:
 * the account already drained through `terminateAccount`, the pair is
 * closed, and the row stays readable for reference forever.
 */
export function groupAccounts<T extends GroupableAccount>(
  rows: T[],
): AccountGroup<T>[] {
  const out: AccountGroup<T>[] = [];
  for (const category of CATEGORY_ORDER) {
    const phases = PHASES_BY_CATEGORY[category];
    const active: Record<string, T[]> = {};
    for (const p of phases) active[p.key] = [];
    const archived: T[] = [];

    for (const row of rows) {
      const cat = categorizeAccount({
        challenge_type: row.challengeType,
        steps: row.steps,
        platform_type: row.platformType ?? "",
      });
      if (cat !== category) continue;
      if (row.outcome != null) {
        archived.push(row);
        continue;
      }
      if (category === "other") {
        active["__other__"]!.push(row);
        continue;
      }
      // Unknown / null phase falls into the first defined phase so it
      // still shows up instead of disappearing.
      const bucket =
        phases.find((p) => p.key === row.phase)?.key ?? phases[0]!.key;
      active[bucket]!.push(row);
    }

    const activeCount = Object.values(active).reduce(
      (n, arr) => n + arr.length,
      0,
    );
    if (activeCount === 0 && archived.length === 0) continue;

    out.push({
      category,
      label: CATEGORY_LABEL[category],
      phases,
      active,
      archived,
      totals: { active: activeCount, archived: archived.length },
    });
  }
  return out;
}
